import { jsPDF } from "jspdf";
import { imgEncabezado2 } from "../../../helpers/imgBase64.js";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrency = (amount) => {
  if (!amount) return "$0.00";
  return `$${amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const groupBitacoraByConceptos = (bitacora, embarcacion) => {
  const conceptos = {};

  // Calcular totales generales para embarque/desembarque de pasajeros
  const totalPasajeros = bitacora.reduce((total, registro) => {
    return total + registro.embarque_px + registro.desembarque_px;
  }, 0);

  const totalTarifaPasajeros = bitacora.reduce((total, registro) => {
    return total + registro.detail.tarifa_pasajeros;
  }, 0);

  const precioUnitarioPasajeros =
    totalPasajeros > 0 ? totalTarifaPasajeros / totalPasajeros : 0;

  // Calcular totales generales para embarque/desembarque de material (muellaje)
  const totalMaterial = bitacora.reduce((total, registro) => {
    return total + registro.embarque_mat + registro.desembarque_mat;
  }, 0);

  const totalTarifaMuellaje = bitacora.reduce((total, registro) => {
    return total + registro.detail.muellaje;
  }, 0);

  const precioUnitarioMuellaje =
    totalMaterial > 0 ? totalTarifaMuellaje / totalMaterial : 0;

  // Calcular totales generales para movimientos internos
  const totalMovimientosInternos = bitacora.reduce((total, registro) => {
    return total + registro.movimiento_interno;
  }, 0);

  const totalTarifaMovInternos = bitacora.reduce((total, registro) => {
    return total + registro.detail.tarifa_mov_intern;
  }, 0);

  const precioUnitarioMovInternos =
    totalMovimientosInternos > 0
      ? totalTarifaMovInternos / totalMovimientosInternos
      : 0;

  // Calcular totales generales para cuota de arribo
  const totalCuotasArribo = bitacora.reduce((total, registro) => {
    return total + (registro.atraque_desatraque ? 1 : 0);
  }, 0);

  const totalTarifaCuotaArribo = bitacora.reduce((total, registro) => {
    return total + registro.detail.cuota_arribo;
  }, 0);

  const precioUnitarioCuotaArribo =
    totalCuotasArribo > 0 ? totalTarifaCuotaArribo / totalCuotasArribo : 0;

  // Agregar concepto consolidado de Cuota de Arribo
  if (totalCuotasArribo > 0) {
    conceptos["Cuota de Arribo"] = {
      nombre: "Cuota de Arribo",
      registros: [
        {
          fecha: "Consolidado",
          fechaDesatraque: "",
          cantidad: totalCuotasArribo,
          precio: precioUnitarioCuotaArribo,
          total: totalTarifaCuotaArribo,
          numeroRegistro: "Total",
        },
      ],
      totalGeneral: totalTarifaCuotaArribo,
    };
  }

  // Agregar concepto consolidado de Embarque/Desembarque PX
  if (totalPasajeros > 0) {
    conceptos["Embarque/Desembarque PX"] = {
      nombre: "Embarque/Desembarque PX",
      registros: [
        {
          fecha: "Consolidado",
          fechaDesatraque: "",
          cantidad: totalPasajeros,
          precio: precioUnitarioPasajeros,
          total: totalTarifaPasajeros,
          numeroRegistro: "Total",
        },
      ],
      totalGeneral: totalTarifaPasajeros,
    };
  }

  // Agregar concepto consolidado de Muellaje
  if (totalMaterial > 0) {
    conceptos["Muellaje"] = {
      nombre: "Muellaje",
      registros: [
        {
          fecha: "Consolidado",
          fechaDesatraque: "",
          cantidad: totalMaterial,
          precio: precioUnitarioMuellaje,
          total: totalTarifaMuellaje,
          numeroRegistro: "Total",
        },
      ],
      totalGeneral: totalTarifaMuellaje,
    };
  }

  // Agregar concepto consolidado de Movimientos Internos
  if (totalMovimientosInternos > 0) {
    conceptos["Movimientos Internos"] = {
      nombre: "Movimientos Internos",
      registros: [
        {
          fecha: "Consolidado",
          fechaDesatraque: "",
          cantidad: totalMovimientosInternos,
          precio: precioUnitarioMovInternos,
          total: totalTarifaMovInternos,
          numeroRegistro: "Total",
        },
      ],
      totalGeneral: totalTarifaMovInternos,
    };
  }

  bitacora.forEach((registro, index) => {
    const detail = registro.detail;
    const fechaAtraque = formatDateTime(registro.atraque);
    const fechaDesatraque = registro.desatraque
      ? formatDateTime(registro.desatraque)
      : "N/A";

    // Verificar si es el último registro y si tiene desatraque
    const esUltimoRegistro = index === bitacora.length - 1;
    const tieneDesatraque = registro.desatraque !== null;

    // Agrupar cada concepto (excluyendo los que ya se manejan consolidados)
    const conceptosRegistro = [
      {
        nombre: "Cuota de Atraque",
        cantidad: detail.estadia,
        precio:
          detail.estadia * (embarcacion?.eslora || 1) > 0
            ? detail.tarifa_atraque /
              (detail.estadia * (embarcacion?.eslora || 1))
            : 0,
        total: detail.tarifa_atraque,
        // Incluir fecha de desatraque solo en el último registro
        fechaCompleta:
          esUltimoRegistro && tieneDesatraque
            ? `${fechaAtraque} - ${fechaDesatraque}`
            : fechaAtraque,
      },
      {
        nombre: "Amarre/Desamarre",
        cantidad: registro.atraque_desatraque ? 1 : 0,
        precio: detail.tarifa_amarre_desamarre,
        total: detail.tarifa_amarre_desamarre,
      },
    ];

    conceptosRegistro.forEach((concepto) => {
      if (concepto.total > 0) {
        const key = concepto.nombre;
        if (!conceptos[key]) {
          conceptos[key] = {
            nombre: concepto.nombre,
            registros: [],
            totalGeneral: 0,
          };
        }
        conceptos[key].registros.push({
          fecha: concepto.fechaCompleta || fechaAtraque,
          fechaDesatraque: fechaDesatraque,
          cantidad: concepto.cantidad,
          precio: concepto.precio,
          total: concepto.total,
          numeroRegistro: index + 1,
        });
        conceptos[key].totalGeneral += concepto.total;
      }
    });
  });

  return conceptos;
};

const formatFolio = (folio, muelle, created) => {
  // Formateo básico: padding con ceros a 4 dígitos
  const folio_number = folio.toString().padStart(4, "0");
  const date = new Date(created);
  // Extracción de componentes de fecha en formato UTC
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();

  // Lógica de negocio: clasificación geográfica de muelles
  let muelle_folio = "";
  // Muelles de zona Chiltepec
  if (
    muelle == "CHILTEPEC" ||
    muelle == "MUELLE DE TANQUES" ||
    muelle == "MUELLE PESCADORES CHILTEPEC"
  ) {
    muelle_folio = "CH";
  } else {
    // Muelles de zona Frontera (por defecto)
    muelle_folio = "FR";
  }

  // Sistema de codificación por tipo de muelle (normativa APITAB)
  let clave_muelle = "";
  switch (muelle) {
    case "ESPADAÑAL":
      clave_muelle = "PAR"; // Particular
      break;
    case "CENTRO DE REPARACIONES NAVALES":
      clave_muelle = "PAR";
      break;
    case "MUELLE PESCADORES FRONTERA":
      clave_muelle = "DEP";
      break;
    case "MUELLE FISCAL":
      clave_muelle = "FIS";
      break;
    case "PARTICULAR":
      clave_muelle = "PAR";
      break;
    case "MUELLE DE TANQUES":
      clave_muelle = "PAR";
      break;
    case "MUELLE PESCADORES CHILTEPEC":
      clave_muelle = "PES";
      break;
    case "SANCHEZ MAGALLANES":
      clave_muelle = "PAR";
      break;
    case "MUELLE COSUGO":
      clave_muelle = "PAR";
      break;
    case "MUELLE PRIVADO CARLOS DAVID":
      clave_muelle = "PAR";
      break;
  }

  // Regla de negocio especial: folios <= 75 usan formato simplificado
  if (folio <= 75) {
    return folio_number;
  }

  // Formato completo para folios > 75: ajuste numérico y codificación completa
  let otro_folio = folio_number - 75;
  const folio_number_2 = otro_folio.toString().padStart(4, "0");
  // Formato final: FOLIO/ZONA/TIPO/FECHA
  return `${folio_number_2}/${muelle_folio}/${clave_muelle}/${day}${month}${year}`;
};

export const proforma = (item) => {
  try {
    console.log(item);
    const doc = new jsPDF("p", "pt", "letter");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    let currentY = 40;

    // Header con logo y QR
    doc.addImage(imgEncabezado2, "JPEG", margin, currentY, 230, 60);

    currentY += 80;

    // Título del documento
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(16);
    const title = "PROFORMA DE SERVICIOS PORTUARIOS";
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, currentY);

    currentY += 30;

    // Información general
    doc.setFont("Helvetica", "normal");
    doc.setFontSize(10);

    // Folio
    doc.text(
      `Folio de Solicitud: ${formatFolio(
        item.folio_solicitud,
        item.muelle,
        item.createdAt
      )}`,
      margin,
      currentY
    );
    doc.text(
      `Fecha: ${formatDate(item.proforma.createdAt)}`,
      pageWidth - 150,
      currentY
    );
    currentY += 20;

    // Información de la agencia
    doc.setFont("Helvetica", "bold");
    doc.text("FACTURAR A:", margin, currentY);
    currentY += 15;

    doc.setFont("Helvetica", "normal");
    doc.text(`${item.agencia.nombre}`, margin, currentY);
    currentY += 12;
    doc.text(`RFC: ${item.agencia.rfc}`, margin, currentY);
    currentY += 20;

    // Información de la embarcación
    doc.setFont("Helvetica", "bold");
    doc.text("EMBARCACIÓN:", margin, currentY);
    currentY += 15;

    doc.setFont("Helvetica", "normal");
    doc.text(`Nombre: ${item.embarcacion?.nombre || "N/A"}`, margin, currentY);
    doc.text(
      `Bandera: ${item.embarcacion?.bandera || "N/A"}`,
      margin + 200,
      currentY
    );
    currentY += 12;
    doc.text(
      `Armador: ${item.embarcacion?.armador || "N/A"}`,
      margin,
      currentY
    );
    currentY += 12;
    doc.text(`Eslora: ${item.embarcacion?.eslora || "N/A"}`, margin, currentY);
    currentY += 30;

    // Tabla de servicios
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(12);
    doc.text("DETALLE DE SERVICIOS", margin, currentY);
    currentY += 20;

    // Headers de la tabla
    const colWidths = [250, 80, 80, 100];
    const colPositions = [
      margin,
      margin + colWidths[0],
      margin + colWidths[0] + colWidths[1],
      margin + colWidths[0] + colWidths[1] + colWidths[2],
    ];

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(9);

    // Header background
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, currentY - 5, pageWidth - 2 * margin, 20, "F");

    doc.text("CONCEPTO", colPositions[0] + 5, currentY + 10);
    doc.text("CANTIDAD", colPositions[1] + 5, currentY + 10);
    doc.text("PRECIO UNIT.", colPositions[2] + 5, currentY + 10);
    doc.text("IMPORTE", colPositions[3] + 5, currentY + 10);

    // Línea separadora
    doc.setDrawColor(0, 0, 0);
    doc.line(margin, currentY + 15, pageWidth - margin, currentY + 15);

    currentY += 25;

    // Agrupar datos de bitácora
    const conceptosAgrupados = groupBitacoraByConceptos(
      item.proforma.bitacora,
      item.embarcacion
    );
    let totalGeneral = 0;

    doc.setFont("Helvetica", "normal");
    doc.setFontSize(8);

    Object.values(conceptosAgrupados).forEach((concepto) => {
      // Nombre del concepto
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(9);
      doc.text(concepto.nombre, colPositions[0] + 5, currentY);

      // Total del concepto
      doc.text(
        formatCurrency(concepto.totalGeneral),
        colPositions[3] + 5,
        currentY
      );
      currentY += 12;

      // Detalle de cada registro
      doc.setFont("Helvetica", "normal");
      doc.setFontSize(7);
      concepto.registros.forEach((registro) => {
        const descripcion = `  Reg. ${registro.numeroRegistro} - ${registro.fecha}`;
        doc.text(descripcion, colPositions[0] + 10, currentY);
        doc.text(registro.cantidad.toString(), colPositions[1] + 5, currentY);
        doc.text(
          formatCurrency(registro.precio),
          colPositions[2] + 5,
          currentY
        );
        doc.text(formatCurrency(registro.total), colPositions[3] + 5, currentY);
        currentY += 10;

        // Nueva página si es necesario
        if (currentY > pageHeight - 100) {
          doc.addPage();
          currentY = 40;
        }
      });

      totalGeneral += concepto.totalGeneral;
      currentY += 5;
    });

    // Línea antes del total
    doc.setDrawColor(0, 0, 0);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 15;

    // Total general
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(12);
    doc.text("TOTAL:", colPositions[2] + 5, currentY);
    doc.text(formatCurrency(totalGeneral), colPositions[3] + 5, currentY);
    currentY += 30;

    // Estado de pago
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(10);
    const estadoPago = item.proforma.pagado ? "PAGADO" : "PENDIENTE DE PAGO";
    const colorPago = item.proforma.pagado ? [0, 128, 0] : [255, 0, 0];
    doc.setTextColor(...colorPago);
    doc.text(`ESTADO: ${estadoPago}`, margin, currentY);
    doc.setTextColor(0, 0, 0);
    currentY += 30;

    // Información adicional
    doc.setFont("Helvetica", "normal");
    doc.setFontSize(8);
    doc.text("NOTAS:", margin, currentY);
    currentY += 12;
    doc.text("- Los precios incluyen IVA donde aplique", margin + 10, currentY);
    currentY += 10;
    doc.text(
      "- Esta proforma tiene vigencia de 30 días",
      margin + 10,
      currentY
    );
    currentY += 10;
    doc.text(
      "- Para cualquier aclaración favor de contactar al área de facturación",
      margin + 10,
      currentY
    );
    currentY += 30;

    // Footer
    doc.setFont("Helvetica", "normal");
    doc.setFontSize(8);
    const footerY = pageHeight - 40;
    doc.text(
      "Administración Portuaria Integral de Tabasco S.A. de C.V.",
      (pageWidth -
        doc.getTextWidth(
          "Administración Portuaria Integral de Tabasco S.A. de C.V."
        )) /
        2,
      footerY
    );
    doc.text(
      "Recinto Fiscal Autorizado Pino Suárez Esq. Muelle Fiscal Col. Centro C.P. 86751 Frontera, Tabasco",
      (pageWidth -
        doc.getTextWidth(
          "Recinto Fiscal Autorizado Pino Suárez Esq. Muelle Fiscal Col. Centro C.P. 86751 Frontera, Tabasco"
        )) /
        2,
      footerY + 10
    );
    doc.text(
      "e-mail: administracion@apitabasco.mx, Tel.: 913-33-2-12-88 y 913-33-2-08-05",
      (pageWidth -
        doc.getTextWidth(
          "e-mail: administracion@apitabasco.mx, Tel.: 913-33-2-12-88 y 913-33-2-08-05"
        )) /
        2,
      footerY + 20
    );

    return doc.output("blob");
  } catch (error) {
    console.error("Error generando el PDF:", error);
    throw new Error("No se pudo generar el PDF de la proforma");
  }
};

export function generateProformaUrl(item) {
  const blob = proforma(item);
  return URL.createObjectURL(blob);
}
